import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectUserProfile } from "../../features/authentication/authenticationSlice";
import { selectCurrentInstructor } from "../../features/instructors/instructors-slice";
import {
  fetchCurrentInstructor,
  updateInstructorLicenceNumber,
} from "../../features/instructors/slice-extensions/instructors-slice-extension";
import { selectOrganization } from "../../features/organization/organization-slice";
import { OrganizationKind } from "../../features/organization/enums/OrganizationKind";

const MAX_LENGTH = 20;

/**
 * The signed-in instructor's licence number ("FL-Nr." in de), printed on training
 * records. Shown only in a driving school (kind Fahrschule), so a music teacher never
 * sees it — a number already on file stays stored but hidden.
 */
const LicenceNumberEditor = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const instructorId = useAppSelector(selectUserProfile)?.instructorId;
  const organization = useAppSelector(selectOrganization);
  const current = useAppSelector(selectCurrentInstructor);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (instructorId) dispatch(fetchCurrentInstructor(instructorId));
  }, [dispatch, instructorId]);

  useEffect(() => {
    setValue(current?.licenceNumber ?? "");
  }, [current?.licenceNumber]);

  const saved = current?.licenceNumber ?? "";
  const visible = organization?.kind === OrganizationKind.Fahrschule;
  if (!current || !visible) return null;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const trimmed = value.trim();
    const result = await dispatch(updateInstructorLicenceNumber(trimmed === "" ? null : trimmed));
    setBusy(false);
    if (updateInstructorLicenceNumber.fulfilled.match(result)) {
      toast.success(t("settings.licenceNumber.saved"));
    } else {
      setError(result.payload?.message ?? t("settings.licenceNumber.failed"));
    }
  };

  return (
    <form onSubmit={save} className="mt-4 flex flex-col gap-1 border-t border-grey-90 pt-4 dark:border-primary-30">
      <label htmlFor="licence-number" className="text-sm font-medium text-grey-20 dark:text-white">
        {t("settings.licenceNumber.label")}
      </label>
      {/* The field keeps room for a whole number (FL-0000-TEST); the button wraps below it
          rather than squeezing it when the card is narrow. */}
      <div className="flex flex-wrap gap-2">
        <input
          id="licence-number"
          className="input min-w-0 grow basis-32 px-3"
          value={value}
          maxLength={MAX_LENGTH}
          onChange={(event) => setValue(event.target.value)}
          disabled={busy}
        />
        <button type="submit" className="btn btn-secondary px-4" disabled={busy || value.trim() === saved}>
          {t("common.save", "Save")}
        </button>
      </div>
      <p className="text-xs text-grey-50 dark:text-grey-70">{t("settings.licenceNumber.hint")}</p>
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    </form>
  );
};

export default LicenceNumberEditor;
